import type { PiezaDeConcepto } from '@supernova/shared';

/**
 * Avance de obra en 3D (OPUS ci, idea 11): la geometría que necesita el «video» y que `@supernova/shared` no tiene.
 * Todo puro, sin three ni DOM (se prueba en `avanceObra4d.test.ts`). Las coordenadas son las del visor IFC: metros,
 * Y hacia arriba.
 */

/** minX, minY, minZ, maxX, maxY, maxZ. */
export type CajaDePieza = readonly [number, number, number, number, number, number];

/**
 * El orden en que se construyen las piezas de UN concepto: de abajo hacia arriba y, a la misma altura, de un lado al
 * otro del plano. Es el orden con que `repartirPiezas` completa las primeras y deja una a medias. Lo que no tiene
 * caja (no vino en el modelo) va al final, en su orden de entrada. La tolerancia de 1 mm es ruido numérico de los
 * puntos flotantes del modelo, no un umbral de obra.
 */
export function ordenDeConstruccion(piezas: readonly PiezaDeConcepto[], cajas: ReadonlyMap<number, CajaDePieza>): PiezaDeConcepto[] {
  const MM = 1e-3;
  const conIndice = piezas.map((p, i) => ({ p, i, c: cajas.get(Number(p.id)) }));
  conIndice.sort((a, b) => {
    if (!a.c && !b.c) return a.i - b.i;
    if (!a.c) return 1;
    if (!b.c) return -1;
    const dy = a.c[1] - b.c[1];
    if (Math.abs(dy) > MM) return dy;
    return a.c[0] - b.c[0] || a.c[2] - b.c[2] || a.i - b.i;
  });
  return conIndice.map((x) => x.p);
}

export interface CorteDePieza {
  /** 0 = X, 1 = Y (arriba), 2 = Z. */
  eje: 0 | 1 | 2;
  /** Coordenada del plano de corte: lo que queda por debajo de ella (en ese eje) es lo ya construido. */
  corte: number;
}

/**
 * Hacia dónde crece una pieza y hasta dónde va con el avance `f` (0 a 1).
 *
 * Sin tabla de tipos: sale de las medidas de la propia pieza. Si es más ALTA que su lado horizontal menor (un muro,
 * una columna, una ventana) se construye de abajo hacia arriba. Si no (una losa, una cubierta, una trabe) avanza a
 * lo largo de su lado horizontal mayor. Un muro tiene el espesor como lado menor, una losa su espesor como altura.
 */
export function corteDePieza(caja: CajaDePieza, f: number): CorteDePieza {
  const [x0, y0, z0, x1, y1, z1] = caja;
  const dx = x1 - x0;
  const dy = y1 - y0;
  const dz = z1 - z0;
  const t = Math.max(0, Math.min(1, f));
  if (dy >= Math.min(dx, dz)) return { eje: 1, corte: y0 + t * dy };
  return dx >= dz ? { eje: 0, corte: x0 + t * dx } : { eje: 2, corte: z0 + t * dz };
}

/** Une las cajas de los trozos de una misma pieza (marco + vidrio): una sola caja por pieza. */
export function unirCajas(a: CajaDePieza | undefined, b: CajaDePieza): CajaDePieza {
  if (!a) return b;
  return [Math.min(a[0], b[0]), Math.min(a[1], b[1]), Math.min(a[2], b[2]), Math.max(a[3], b[3]), Math.max(a[4], b[4]), Math.max(a[5], b[5])];
}

// ---------------------------------------------------------------------------------------------- textos y datos para la pantalla

/** El encabezado de «Por revisar» según de dónde vino el avance (sin nombres largos de producto ni de proveedores). */
export function textoPorRevisar(origen: 'm1' | 'm2'): string {
  const de = origen === 'm2' ? 'de OPUS Control' : 'de OPUS Presupuesto';
  return `Renglones que llegaron ${de} y no se pudieron ligar a un concepto de la hoja. Su importe sí cuenta en la curva.`;
}

/** Lo que lee una persona cuando un renglón de OPUS 27 no se pudo ligar a un concepto de la hoja. */
export function textoDeMotivo(motivo: string | null): string {
  switch (motivo) {
    case 'clave_repetida':
      return 'la clave está repetida en la hoja y no se sabe a cuál corresponde';
    case 'clave_no_encontrada':
      return 'no hay un concepto con esa clave en la hoja';
    case 'posicion_no_coincide':
      return 'el concepto de esa posición tiene otra clave';
    case 'concepto_borrado':
      return 'el concepto se quitó de la hoja al rearmarla';
    case 'concepto_no_encontrado':
      return 'el concepto ya no existe en esta hoja';
    case 'agrupador':
      return 'es un agrupador (capítulo), no un concepto con avance';
    default:
      return 'no se pudo ligar a la hoja';
  }
}

/** El avance por pieza que entiende el visor: id de pieza (texto) → número de expressID, con lo hecho de cada una. */
export function mapaParaElVisor(piezas: ReadonlyMap<string, { f: number }>): Map<number, number> {
  const out = new Map<number, number>();
  for (const [id, x] of piezas) {
    const n = Number(id);
    if (Number.isInteger(n)) out.set(n, x.f);
  }
  return out;
}

/**
 * Cada cuántos milisegundos se repinta el modelo mientras se reproduce. Repintar recorre todas las piezas del modelo:
 * a 60 cuadros por segundo un modelo grande no alcanza; la animación se ve fluida igual porque la fecha sigue avanzando
 * y cada repintado salta al estado de esa fecha.
 */
export const MS_ENTRE_REPINTADOS = 80;

/** Segundos que dura el video completo de la obra a cada velocidad. */
export const SEGUNDOS_DEL_VIDEO = { lenta: 40, normal: 20, rapida: 10 } as const;
export type VelocidadDelVideo = keyof typeof SEGUNDOS_DEL_VIDEO;

/**
 * Un paso de la reproducción: de la fecha `t` (días) avanza `dtSegundos` a la velocidad elegida sobre el rango
 * completo. Llega al final y se queda ahí (`termino`).
 */
export function pasoDeReproduccion(
  t: number,
  dtSegundos: number,
  rango: { desde: number; hasta: number },
  velocidad: VelocidadDelVideo,
): { t: number; termino: boolean } {
  const dias = rango.hasta - rango.desde;
  if (dias <= 0) return { t: rango.hasta, termino: true };
  const sig = t + (dtSegundos / SEGUNDOS_DEL_VIDEO[velocidad]) * dias;
  return sig >= rango.hasta ? { t: rango.hasta, termino: true } : { t: Math.max(rango.desde, sig), termino: false };
}

/** Un día (días desde 1970, UTC) como texto corto para la pantalla: «14 oct 2026». */
export function etiquetaDeDia(dia: number): string {
  return new Date(dia * 86_400_000).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }).replace(/\./g, '');
}

/** El mes y el año de un día: «oct 2026» (para los rótulos del eje de la curva). */
export function etiquetaDeMes(dia: number): string {
  return new Date(dia * 86_400_000).toLocaleDateString('es-MX', { month: 'short', year: 'numeric', timeZone: 'UTC' }).replace(/\./g, '');
}

/**
 * Un nombre legible para la obra de la que vienen los avances. m1 identifica su obra como «<id de la hoja>/<base de datos>»
 * (p. ej. `b4dd…/C:\ECOSOFT\PROYECTOS\CASA FZK.MDF`): se muestra solo el nombre de la base, sin el id, la carpeta ni la
 * extensión («CASA FZK»). Lo que no tenga esa forma (el origen de ejemplo `SINT-1`, otra fuente) se deja como viene.
 */
export function nombreDeLaObra(obraRef: string): string {
  const sinHoja = obraRef.replace(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\//i, '');
  const ultimo = sinHoja.split(/[\\/]/).pop() ?? sinHoja;
  const nombre = ultimo.replace(/\.mdf$/i, '').trim();
  return nombre === '' ? obraRef : nombre;
}

/**
 * Las piezas de un concepto en un dibujo DWG/DXF a partir de sus handles: se ordenan de izquierda a derecha y de abajo hacia arriba
 * (el orden en que «se construyen») y cada una pesa lo que mide en el dibujo (la diagonal de su caja), para que el porcentaje pintado
 * se parezca al real.
 */
export function piezasDwgDeHandles(
  handles: Iterable<string>,
  censo: { centro: (h: string) => { x: number; y: number } | undefined; tamano: (h: string) => number | undefined },
): PiezaDeConcepto[] {
  return [...new Set(handles)]
    .map((id) => ({ id, ctr: censo.centro(id) }))
    .sort((a, b) => (a.ctr?.x ?? Infinity) - (b.ctr?.x ?? Infinity) || (a.ctr?.y ?? Infinity) - (b.ctr?.y ?? Infinity))
    .map(({ id }) => ({ id, peso: Math.max(censo.tamano(id) ?? 0, 1e-6) }));
}

/** ¿El centro cae dentro de la caja de la vista? Sin centro conocido: sí (el mismo criterio que el visor). */
export function dentroDeCaja(
  centro: { x: number; y: number } | undefined,
  caja: readonly [number, number, number, number],
): boolean {
  if (!centro) return true;
  const [x0, y0, x1, y1] = caja;
  return centro.x >= Math.min(x0, x1) && centro.x <= Math.max(x0, x1) && centro.y >= Math.min(y0, y1) && centro.y <= Math.max(y0, y1);
}

/**
 * Huella de un nombre de capa o bloque: sha256 del nombre EXACTO (utf-8), primeros 16 caracteres hex. Es la misma que calcula la API para
 * la foto del enlace del cliente (que no lleva nombres legibles). Necesita contexto seguro (https o localhost).
 */
export async function huellaDeNombre(nombre: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(nombre));
  return [...new Uint8Array(buf)]
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
    .slice(0, 16);
}
